import type { MetaFunction } from 'react-router'
import { Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { ArrowLeft, FileText, ExternalLink, Database } from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Sources & méthodologie RGE — AfficheServices' },
  { name: 'description', content: 'Méthodologie, sources de données et fréquence de mise à jour pour les informations RGE et CEE publiées sur AfficheServices.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/rge/sources' },
]

export default function RGESourcesPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'RGE', url: 'https://www.afficheservices.fr/rge' },
        { name: 'Sources', url: 'https://www.afficheservices.fr/rge/sources' },
      ]} />

      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-12 text-white sm:py-16">
        <div className="mx-auto max-w-3xl">
          <Link to="/rge" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Retour RGE
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-slate-300">
            <FileText className="h-3.5 w-3.5" /> Transparence
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-4xl">Sources &amp; méthodologie</h1>
          <p className="mt-3 max-w-2xl text-base text-slate-300">
            D'où viennent nos données RGE, CEE et MaPrimeRénov'. Comment nous les vérifions
            et à quelle fréquence nous les mettons à jour.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-4 py-12 lg:px-6">
        <article className="space-y-10 text-sm leading-relaxed text-slate-700">
          <section>
            <h2 className="text-xl font-bold text-slate-900">Sources primaires</h2>
            <p className="mt-3">
              Toutes les informations publiées sur AfficheServices concernant les aides à la rénovation
              énergétique, les qualifications RGE et les primes CEE proviennent exclusivement de sources
              officielles publiques. Nous ne publions aucune donnée commerciale, ni indicateur issu
              d'acteurs privés non identifiables.
            </p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3 rounded-xl border border-border bg-white p-4">
                <Database className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="font-semibold text-slate-900">ADEME — France Rénov'</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Annuaire officiel des artisans RGE, fiches CEE, guides MaPrimeRénov'.
                  </p>
                  <a
                    href="https://france-renov.gouv.fr"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    france-renov.gouv.fr <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-xl border border-border bg-white p-4">
                <Database className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="font-semibold text-slate-900">data.ademe.fr — Liste des entreprises RGE</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Jeu de données ouvert sous licence Etalab 2.0, mis à jour chaque trimestre par l'ADEME.
                  </p>
                  <a
                    href="https://data.ademe.fr/datasets/liste-des-entreprises-rge-2"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    data.ademe.fr/datasets/liste-des-entreprises-rge-2 <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-xl border border-border bg-white p-4">
                <Database className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="font-semibold text-slate-900">Ministère de la Transition Écologique</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Arrêtés et décrets fixant les barèmes CEE, Coup de pouce et MaPrimeRénov'.
                  </p>
                  <a
                    href="https://www.ecologie.gouv.fr/politiques-publiques/dispositif-certificats-deconomies-denergie"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    ecologie.gouv.fr <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-xl border border-border bg-white p-4">
                <Database className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="font-semibold text-slate-900">ANAH — MaPrimeRénov'</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Barèmes officiels par type de geste et catégorie de revenus (Bleu, Jaune, Violet, Rose).
                  </p>
                  <a
                    href="https://www.anah.gouv.fr"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    anah.gouv.fr <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-xl border border-border bg-white p-4">
                <Database className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="font-semibold text-slate-900">INSEE — Base SIRENE</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Données de référence sur les artisans : SIRET, NAF, adresse, forme juridique, date de création.
                  </p>
                  <a
                    href="https://www.sirene.fr"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    sirene.fr <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">Fréquence de mise à jour</h2>
            <div className="mt-3 space-y-2">
              <p>
                <strong className="font-semibold text-slate-900">Barèmes CEE et MaPrimeRénov' :</strong> contrôlés
                à chaque publication d'un nouvel arrêté au Journal Officiel, puis vérification trimestrielle systématique.
              </p>
              <p>
                <strong className="font-semibold text-slate-900">Annuaire RGE :</strong> la base de référence
                ADEME est mise à jour chaque trimestre. Notre snapshot local est rafraîchi dans les 7 jours
                suivant une publication.
              </p>
              <p>
                <strong className="font-semibold text-slate-900">Base SIRENE :</strong> synchronisée mensuellement
                via l'API publique recherche-entreprises.api.gouv.fr.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">Limitations connues</h2>
            <p className="mt-3">
              Nos montants CEE sont des moyennes indicatives du marché. Les primes réelles varient selon l'opérateur
              (EDF, TotalEnergies, mandataires) et la zone climatique (H1, H2, H3). Pour un chiffrage personnalisé,
              utilisez{' '}
              <a
                href="https://mesaidesreno.gouv.fr"
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-primary hover:underline"
              >
                mesaidesreno.gouv.fr
              </a>
              {' '}ou contactez un conseiller France Rénov' (gratuit et neutre, 0 808 800 700).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">Signaler une erreur</h2>
            <p className="mt-3">
              Vous avez repéré une information obsolète ou incorrecte ? Écrivez-nous à{' '}
              <a href="mailto:contact@afficheservices.fr" className="font-semibold text-primary hover:underline">
                contact@afficheservices.fr
              </a>
              . Les corrections sont traitées sous 48h.
            </p>
          </section>
        </article>
      </div>

      <Footer />
    </div>
  )
}
